/**
 * Currency Utilities
 * Extracts country from address and returns appropriate currency symbol
 */

/**
 * Extract country name from address string
 * Address format: "address, city, state, country" or "address, country"
 */
export const extractCountryFromAddress = (address) => {
  if (!address || typeof address !== 'string') {
    return null;
  }

  const addressLower = address.toLowerCase();
  let country = null;
  
  // FIRST: Check for Indian states/regions (common in Indian addresses)
  // This must be checked FIRST to avoid false matches like "USA" in "Uttarakhand"
  const indianStates = [
    'uttarakhand', 'uttar pradesh', 'rajasthan', 'gujarat', 'maharashtra', 'karnataka',
    'tamil nadu', 'kerala', 'west bengal', 'odisha', 'andhra pradesh', 'telangana',
    'bihar', 'jharkhand', 'assam', 'punjab', 'haryana', 'himachal pradesh',
    'madhya pradesh', 'chhattisgarh', 'goa', 'manipur', 'meghalaya', 'mizoram',
    'nagaland', 'sikkim', 'tripura', 'arunachal pradesh', 'delhi', 'jammu and kashmir',
    'ladakh', 'puducherry', 'andaman and nicobar', 'daman and diu', 'dadra and nagar haveli',
    'lakshadweep', 'chandigarh'
  ];
  
  for (const state of indianStates) {
    if (addressLower.includes(state)) {
      country = 'India';
      return country; // Return immediately to avoid false matches
    }
  }

  // Split by comma and get the last part (usually country)
  const parts = address.split(',').map(part => part.trim());
  
  // Try to find common country names (but only if Indian state not found)
  const commonCountries = [
    // Middle East
    'Saudi Arabia', 'United Arab Emirates', 'UAE', 'Qatar', 'Kuwait', 'Bahrain', 'Oman', 
    'Yemen', 'Jordan', 'Lebanon', 'Syria', 'Iraq', 'Iran', 'Israel', 'Palestine', 'Turkey',
    // South Asia
    'India', 'Pakistan', 'Bangladesh', 'Sri Lanka', 'Nepal', 'Bhutan', 'Maldives', 'Afghanistan',
    // Southeast Asia
    'Singapore', 'Malaysia', 'Thailand', 'Indonesia', 'Philippines', 'Vietnam', 'Myanmar', 
    'Cambodia', 'Laos', 'Brunei', 'East Timor', 'Timor-Leste',
    // East Asia
    'Japan', 'China', 'South Korea', 'North Korea', 'Taiwan', 'Hong Kong', 'Macau', 'Mongolia',
    // North America - Use word boundary matching to avoid false matches
    'United States', 'Canada', 'Mexico', 'Guatemala', 'Belize', 'El Salvador', 'Honduras',
    'Nicaragua', 'Costa Rica', 'Panama', 'Cuba', 'Jamaica', 'Haiti', 'Dominican Republic',
    // South America
    'Brazil', 'Argentina', 'Chile', 'Colombia', 'Peru', 'Venezuela', 'Ecuador', 'Bolivia',
    'Paraguay', 'Uruguay', 'Guyana', 'Suriname', 'French Guiana',
    // Europe
    'United Kingdom', 'UK', 'Ireland', 'France', 'Germany', 'Italy', 'Spain', 'Netherlands',
    'Belgium', 'Switzerland', 'Austria', 'Portugal', 'Greece', 'Poland', 'Czech Republic',
    'Hungary', 'Romania', 'Bulgaria', 'Croatia', 'Serbia', 'Slovakia', 'Slovenia', 'Denmark',
    'Sweden', 'Norway', 'Finland', 'Iceland', 'Luxembourg', 'Malta', 'Cyprus', 'Estonia',
    'Latvia', 'Lithuania', 'Russia', 'Ukraine', 'Belarus', 'Moldova',
    // Africa
    'Egypt', 'South Africa', 'Nigeria', 'Kenya', 'Morocco', 'Tunisia', 'Algeria', 'Ghana',
    'Ethiopia', 'Tanzania', 'Uganda', 'Zimbabwe', 'Botswana', 'Namibia', 'Mauritius',
    // Oceania
    'Australia', 'New Zealand', 'Fiji', 'Papua New Guinea', 'Samoa', 'Tonga', 'Vanuatu'
  ];

  // Check from the end of the address (country is usually at the end)
  // Use word boundary matching for "USA" to avoid matching it in "Uttarakhand"
  for (let i = parts.length - 1; i >= 0; i--) {
    const part = parts[i].toLowerCase();
    for (const countryName of commonCountries) {
      const countryNameLower = countryName.toLowerCase();
      
      // For "USA", use word boundary to avoid false matches
      if (countryNameLower === 'usa' || countryNameLower === 'united states') {
        // Check for "USA" or "United States" as whole words only
        const usaRegex = /\b(usa|united states)\b/i;
        if (usaRegex.test(part) || usaRegex.test(addressLower)) {
          country = countryName;
          break;
        }
      } else {
        // For other countries, check exact match or if part contains the country name
        // But use word boundaries for short country codes to avoid false matches
        if (part === countryNameLower) {
          country = countryName;
          break;
        } else if (countryNameLower.length > 3 && part.includes(countryNameLower)) {
          // Only use includes for longer country names to reduce false matches
          country = countryName;
          break;
        }
      }
    }
    if (country) break;
  }

  // If no match found, try the last part as country
  if (!country && parts.length > 0) {
    const lastPart = parts[parts.length - 1].toLowerCase();
    // Check if last part looks like a country name (not a number, not too short)
    // But exclude if it contains numbers (like postal codes)
    if (lastPart.length > 2 && !/^\d+$/.test(lastPart) && !/\d/.test(lastPart)) {
      country = parts[parts.length - 1];
    }
  }

  return country;
};

/**
 * Get currency symbol based on country
 */
export const getCurrencySymbol = (country) => {
  if (!country) {
    return '₹'; // Default to Indian Rupee
  }

  const countryLower = country.toLowerCase();

  // Middle East
  if (countryLower.includes('saudi arabia')) return 'SAR'; // Saudi Riyal
  if (countryLower.includes('united arab emirates') || countryLower.includes('uae')) return 'د.إ'; // UAE Dirham
  if (countryLower.includes('qatar')) return '﷼'; // Qatari Riyal
  if (countryLower.includes('kuwait')) return 'د.ك'; // Kuwaiti Dinar
  if (countryLower.includes('bahrain')) return '.د.ب'; // Bahraini Dinar
  if (countryLower.includes('oman')) return 'ر.ع.'; // Omani Rial
  if (countryLower.includes('yemen')) return '﷼'; // Yemeni Rial
  if (countryLower.includes('jordan')) return 'د.ا'; // Jordanian Dinar
  if (countryLower.includes('lebanon')) return 'ل.ل'; // Lebanese Pound
  if (countryLower.includes('syria')) return '£'; // Syrian Pound
  if (countryLower.includes('iraq')) return 'ع.د'; // Iraqi Dinar
  if (countryLower.includes('iran')) return '﷼'; // Iranian Rial
  if (countryLower.includes('israel')) return '₪'; // Israeli Shekel
  if (countryLower.includes('palestine')) return '₪'; // Israeli Shekel (used in Palestine)
  if (countryLower.includes('turkey')) return '₺'; // Turkish Lira

  // South Asia
  if (countryLower.includes('india')) return '₹'; // Indian Rupee
  if (countryLower.includes('pakistan')) return '₨'; // Pakistani Rupee
  if (countryLower.includes('bangladesh')) return '৳'; // Bangladeshi Taka
  if (countryLower.includes('sri lanka')) return '₨'; // Sri Lankan Rupee
  if (countryLower.includes('nepal')) return '₨'; // Nepalese Rupee
  if (countryLower.includes('bhutan')) return 'Nu.'; // Bhutanese Ngultrum
  if (countryLower.includes('maldives')) return 'Rf'; // Maldivian Rufiyaa
  if (countryLower.includes('afghanistan')) return '؋'; // Afghan Afghani

  // Southeast Asia
  if (countryLower.includes('singapore')) return 'S$'; // Singapore Dollar
  if (countryLower.includes('malaysia')) return 'RM'; // Malaysian Ringgit
  if (countryLower.includes('thailand')) return '฿'; // Thai Baht
  if (countryLower.includes('indonesia')) return 'Rp'; // Indonesian Rupiah
  if (countryLower.includes('philippines')) return '₱'; // Philippine Peso
  if (countryLower.includes('vietnam')) return '₫'; // Vietnamese Dong
  if (countryLower.includes('myanmar')) return 'K'; // Myanmar Kyat
  if (countryLower.includes('cambodia')) return '៛'; // Cambodian Riel
  if (countryLower.includes('laos')) return '₭'; // Lao Kip
  if (countryLower.includes('brunei')) return 'B$'; // Brunei Dollar
  if (countryLower.includes('east timor') || countryLower.includes('timor-leste')) return '$'; // US Dollar

  // East Asia
  if (countryLower.includes('japan')) return '¥'; // Japanese Yen
  if (countryLower.includes('china')) return '¥'; // Chinese Yuan
  if (countryLower.includes('south korea')) return '₩'; // South Korean Won
  if (countryLower.includes('north korea')) return '₩'; // North Korean Won
  if (countryLower.includes('taiwan')) return 'NT$'; // New Taiwan Dollar
  if (countryLower.includes('hong kong')) return 'HK$'; // Hong Kong Dollar
  if (countryLower.includes('macau')) return 'MOP$'; // Macanese Pataca
  if (countryLower.includes('mongolia')) return '₮'; // Mongolian Tugrik

  // North America
  if (countryLower.includes('united states') || countryLower.includes('usa')) return '$'; // US Dollar
  if (countryLower.includes('canada')) return 'C$'; // Canadian Dollar
  if (countryLower.includes('mexico')) return '$'; // Mexican Peso
  if (countryLower.includes('guatemala')) return 'Q'; // Guatemalan Quetzal
  if (countryLower.includes('belize')) return 'BZ$'; // Belize Dollar
  if (countryLower.includes('el salvador')) return '$'; // US Dollar
  if (countryLower.includes('honduras')) return 'L'; // Honduran Lempira
  if (countryLower.includes('nicaragua')) return 'C$'; // Nicaraguan Córdoba
  if (countryLower.includes('costa rica')) return '₡'; // Costa Rican Colón
  if (countryLower.includes('panama')) return '$'; // US Dollar
  if (countryLower.includes('cuba')) return '$'; // Cuban Peso
  if (countryLower.includes('jamaica')) return 'J$'; // Jamaican Dollar
  if (countryLower.includes('haiti')) return 'G'; // Haitian Gourde
  if (countryLower.includes('dominican republic')) return 'RD$'; // Dominican Peso

  // South America
  if (countryLower.includes('brazil')) return 'R$'; // Brazilian Real
  if (countryLower.includes('argentina')) return '$'; // Argentine Peso
  if (countryLower.includes('chile')) return '$'; // Chilean Peso
  if (countryLower.includes('colombia')) return '$'; // Colombian Peso
  if (countryLower.includes('peru')) return 'S/'; // Peruvian Sol
  if (countryLower.includes('venezuela')) return 'Bs.'; // Venezuelan Bolívar
  if (countryLower.includes('ecuador')) return '$'; // US Dollar
  if (countryLower.includes('bolivia')) return 'Bs.'; // Bolivian Boliviano
  if (countryLower.includes('paraguay')) return '₲'; // Paraguayan Guaraní
  if (countryLower.includes('uruguay')) return '$'; // Uruguayan Peso
  if (countryLower.includes('guyana')) return 'G$'; // Guyanese Dollar
  if (countryLower.includes('suriname')) return 'Sr$'; // Surinamese Dollar

  // Europe
  if (countryLower.includes('united kingdom') || countryLower.includes('uk')) return '£'; // British Pound
  if (countryLower.includes('ireland')) return '€'; // Euro
  if (countryLower.includes('france') || countryLower.includes('germany') || 
      countryLower.includes('italy') || countryLower.includes('spain') || 
      countryLower.includes('netherlands') || countryLower.includes('belgium') ||
      countryLower.includes('portugal') || countryLower.includes('greece') ||
      countryLower.includes('austria') || countryLower.includes('finland') ||
      countryLower.includes('luxembourg') || countryLower.includes('malta') ||
      countryLower.includes('cyprus') || countryLower.includes('estonia') ||
      countryLower.includes('latvia') || countryLower.includes('lithuania') ||
      countryLower.includes('slovakia') || countryLower.includes('slovenia')) return '€'; // Euro
  if (countryLower.includes('switzerland')) return 'CHF'; // Swiss Franc
  if (countryLower.includes('poland')) return 'zł'; // Polish Złoty
  if (countryLower.includes('czech republic') || countryLower.includes('czech')) return 'Kč'; // Czech Koruna
  if (countryLower.includes('hungary')) return 'Ft'; // Hungarian Forint
  if (countryLower.includes('romania')) return 'lei'; // Romanian Leu
  if (countryLower.includes('bulgaria')) return 'лв'; // Bulgarian Lev
  if (countryLower.includes('croatia')) return 'kn'; // Croatian Kuna
  if (countryLower.includes('serbia')) return 'дин'; // Serbian Dinar
  if (countryLower.includes('denmark')) return 'kr'; // Danish Krone
  if (countryLower.includes('sweden')) return 'kr'; // Swedish Krona
  if (countryLower.includes('norway')) return 'kr'; // Norwegian Krone
  if (countryLower.includes('iceland')) return 'kr'; // Icelandic Króna
  if (countryLower.includes('russia')) return '₽'; // Russian Ruble
  if (countryLower.includes('ukraine')) return '₴'; // Ukrainian Hryvnia
  if (countryLower.includes('belarus')) return 'Br'; // Belarusian Ruble
  if (countryLower.includes('moldova')) return 'lei'; // Moldovan Leu

  // Africa
  if (countryLower.includes('egypt')) return '£'; // Egyptian Pound
  if (countryLower.includes('south africa')) return 'R'; // South African Rand
  if (countryLower.includes('nigeria')) return '₦'; // Nigerian Naira
  if (countryLower.includes('kenya')) return 'KSh'; // Kenyan Shilling
  if (countryLower.includes('morocco')) return 'د.م.'; // Moroccan Dirham
  if (countryLower.includes('tunisia')) return 'د.ت'; // Tunisian Dinar
  if (countryLower.includes('algeria')) return 'د.ج'; // Algerian Dinar
  if (countryLower.includes('ghana')) return '₵'; // Ghanaian Cedi
  if (countryLower.includes('ethiopia')) return 'Br'; // Ethiopian Birr
  if (countryLower.includes('tanzania')) return 'TSh'; // Tanzanian Shilling
  if (countryLower.includes('uganda')) return 'USh'; // Ugandan Shilling
  if (countryLower.includes('zimbabwe')) return '$'; // US Dollar
  if (countryLower.includes('botswana')) return 'P'; // Botswana Pula
  if (countryLower.includes('namibia')) return 'N$'; // Namibian Dollar
  if (countryLower.includes('mauritius')) return '₨'; // Mauritian Rupee

  // Oceania
  if (countryLower.includes('australia')) return 'A$'; // Australian Dollar
  if (countryLower.includes('new zealand')) return 'NZ$'; // New Zealand Dollar
  if (countryLower.includes('fiji')) return 'FJ$'; // Fijian Dollar
  if (countryLower.includes('papua new guinea')) return 'K'; // Papua New Guinean Kina
  if (countryLower.includes('samoa')) return 'T'; // Samoan Tālā
  if (countryLower.includes('tonga')) return 'T$'; // Tongan Paʻanga
  if (countryLower.includes('vanuatu')) return 'Vt'; // Vanuatu Vatu

  // Default to Indian Rupee if country not recognized
  return '₹';
};

/**
 * Get currency symbol for a hotel based on its address
 */
export const getHotelCurrencySymbol = (hotel) => {
  if (!hotel || !hotel.address) {
    return '₹'; // Default to INR if no address
  }

  const country = extractCountryFromAddress(hotel.address);
  
  // If country extraction failed (returns null or unrecognized), default to INR
  if (!country) {
    return '₹'; // Default to Indian Rupee
  }
  
  const symbol = getCurrencySymbol(country);
  
  // If getCurrencySymbol returns default (₹) for unrecognized country, that's fine
  // But if it somehow returns something else for null, ensure we default to INR
  return symbol || '₹';
};

/**
 * Known ISO codes with a display symbol. Unmapped codes are shown as the code itself.
 */
const CURRENCY_CODE_SYMBOLS = {
  INR: '₹',
  USD: '$',
  EUR: '€',
  GBP: '£',
  AED: 'د.إ',
  SAR: 'SAR',
  SGD: 'S$',
  AUD: 'A$',
  CAD: 'C$',
  JPY: '¥',
  CNY: '¥',
};

const hasKnownCurrencySymbol = (code) =>
  Object.prototype.hasOwnProperty.call(CURRENCY_CODE_SYMBOLS, code);

/**
 * Get display symbol from ISO currency code (e.g. USD -> $, INR -> ₹).
 * Returns the code itself when no symbol is mapped.
 */
export const getCurrencySymbolFromCode = (currencyCode) => {
  if (!currencyCode || typeof currencyCode !== 'string') {
    return '₹';
  }
  const code = currencyCode.trim().toUpperCase();
  return hasKnownCurrencySymbol(code) ? CURRENCY_CODE_SYMBOLS[code] : code;
};

/**
 * Format a booking/payment amount using the API currency field.
 * Known symbols: $296.13. Unknown codes: THB 296.13
 */
export const formatBookingAmount = (amount, currencyCode = 'INR') => {
  const value = parseFloat(amount) || 0;
  const code = (currencyCode || 'INR').trim().toUpperCase();

  if (code === 'INR') {
    return `${CURRENCY_CODE_SYMBOLS.INR}${Math.round(value)}`;
  }

  const formattedValue = value.toFixed(2);

  if (hasKnownCurrencySymbol(code)) {
    return `${CURRENCY_CODE_SYMBOLS[code]}${formattedValue}`;
  }

  return `${code} ${formattedValue}`;
};

/**
 * Format amounts for invoices/statements with 2 decimal places.
 */
export const formatCurrencyAmount = (amount, currencyCode = 'INR') => {
  const value = parseFloat(amount) || 0;
  const code = (currencyCode || 'INR').trim().toUpperCase();
  const formattedValue = value.toLocaleString(code === 'INR' ? 'en-IN' : 'en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  if (hasKnownCurrencySymbol(code)) {
    return `${CURRENCY_CODE_SYMBOLS[code]}${formattedValue}`;
  }

  return `${code} ${formattedValue}`;
};

